import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiService } from './apiService';

function ImageUploadField({ label, value, onChange, helperText }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const result = await apiService.uploadImage(file, (percent) => setProgress(percent));
      onChange(result.url || result.fileName || '');
    } catch (err) {
      setError(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: false,
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          cursor: 'pointer',
          mb: 1,
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={1} alignItems="center">
          <CloudUploadIcon color="primary" />
          <Typography variant="body2">
            {uploading ? 'Uploading...' : isDragActive ? 'Drop image here' : 'Drag & drop image here or click to browse'}
          </Typography>
        </Stack>
      </Box>

      {uploading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">{progress}%</Typography>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error.main">{error}</Typography>
      )}

      {value && (
        <Box sx={{ mb: 1 }}>
          <Box
            component="img"
            src={value}
            alt="Upload preview"
            sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 1 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={() => onChange('')}
            >
              Remove image
            </Button>
          </Box>
        </Box>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary">{helperText}</Typography>
      )}
    </Box>
  );
}

export default ImageUploadField;
